/* Copyright 2015-2016 The MathWorks, Inc. */

define([
    "dojo/_base/declare",
    "dojo/keys",
    "mw-form/ComboBox",
    "dijit/registry",
    "../mixins/property/MnemonicMixin",
    "../mixins/property/MnemonicKeyNavDirectionMixin",
    "../mixins/AcceptFocusMixin",
    "../mixins/ColumnWidthMixin",
    "../mixins/property/TypeMixin",
    "../mixins/property/DescriptionOverrideMixin",
    "mw-form/mixins/property/ShortcutMixin",
    "mw-form/mixins/property/ValidationMixin"
], function (declare, keys, ComboBox, registry, MnemonicMixin, MnemonicKeyNavDirectionMixin,
             AcceptFocusMixin, ColumnWidthMixin, TypeMixin, DescriptionOverrideMixin, ShortcutMixin, ValidationMixin) {

    return declare([ComboBox, MnemonicMixin, MnemonicKeyNavDirectionMixin, AcceptFocusMixin, ColumnWidthMixin,
        TypeMixin, DescriptionOverrideMixin, ShortcutMixin, ValidationMixin], {
            visualFamily: "default",
            height: 22,
            mnemonicKeyNavDirection: "none",
            _scaleToParentWidth: true,

             // g1993569: Adding this property to be able to distinguish between user interaction and programmatic calls
            // to change the combobox properties.
            _treatAsUserEvent: false,

            // For focus manager
            _setEditableAttr: function(editable) {
                this.inherited(arguments);

                if(editable === false) {
                    this.domNode.setAttribute("data-focus", "refuse");
                } else {
                    this.domNode.setAttribute("data-focus", "accept");
                    this.arrowNode.setAttribute("data-focus", "refuse");
                }
            },

            postCreate: function () {
                this.inherited(arguments);

                // For focus manager
                //TODO: investigate
                if(this.get("editable") === false) {
                    this.domNode.setAttribute("data-focus", "refuse");
                } else {
                    this.domNode.setAttribute("data-focus", "accept");
                    this.get("menu").domNode.setAttribute("data-focus", "refuse");
                }
                this.get("menu").domNode.setAttribute("data-focus", "temporary");


            },

            _handleTextFieldKeyDown: function (e) {

                if (e.keyCode === keys.ESCAPE) {
                    this.emit("textcancelled",{
                        mwEventData: {
                            text: this.inputNode.value
                        }
                    });
                    this.emit("restorefocustolastnonrefuseelement");
                }

                if (e.keyCode === keys.ENTER) {
                    this._treatAsUserEvent =  true;
                    if(this.get("value")===this.inputNode.value) {
                        var selected = this._getSelectedItemFromValue(this.get("value"));

                        if(selected && selected.label===this.inputNode.value && selected.value!==undefined) {
                            this.emit("recommit", {
                                mwEventData: {
                                    value: selected.value
                                }
                            });
                        } else {
                            this.emit("recommit", {
                                mwEventData: {
                                    text: this.get("value")
                                }
                            });
                        }
                    }
                    this.emit("restorefocustolastnonrefuseelement");
                }

                this.inherited(arguments);
                this._treatAsUserEvent =  false;
            },

            // Override from mw-form/ComboBox
            _handleTextFieldBlur: function (e) {
                this._treatAsUserEvent = true;
                this.inherited(arguments);

                // Setting the _treatAsUserEvent flag back to false within a setTimeout since the combobox logic to
                // set the value on blur is within a setTimeout.
                setTimeout(function () {
                    this._treatAsUserEvent = false;
                }.bind(this), 0);
            },

            _handleMenuClick: function (e) {
                this._treatAsUserEvent = true;

                // Get the text of the clicked menu item which is also the label of an item
                var selectedLabel = e.target.textContent;
                // Determine the selected Item using the label
                var selected = this._getSelectedItemFromLabel(selectedLabel);

               if(this.get("value")===selected.value) {
                   this.emit("recommit", {
                       mwEventData: {
                           value: this.get("value")
                       }
                   });
               }

                this.inherited(arguments);

                this._treatAsUserEvent = false;
            },

            // override items setter from mw/form combo box
            _setItemsAttr: function (items) {
                this._treatAsUserEvent = false;

                if (items && items.length > 0) {
                    
                    var valueToSet;                    
                    items.forEach(function (item) {
                        if (item.selected === true) {                            
                            valueToSet = item.value;
                        }
                    }, this);

                    // if valueToSet doesn't come back with a value, then reset "value" to "". 
                    // If it comes back with a value set it to that specific value.
                    // NOTE: the order of calling _initMenuItems and _set("items") matters here. 
                    // See g1659334

                    if (valueToSet === undefined) {
                        this.set("value", "", true);
                        this._initMenuItems(items);
                        this._set("items", items);
                    } else {
                        this._initMenuItems(items);
                        this._set("items", items);
                        this.set("value", valueToSet, true);
                    }                

                } else {

                    this.set("value", "", true);                    
                    this._destroyMenuItems();
                    this._set("items", items);
                }

                if (this.get("editable") === false && this._pendingValue != null) {
                    this.set("value", this._pendingValue, true);
                    this._pendingValue = null;
                }

                // add a data-type to all the menu items each time they are set
                this.menu.getChildren().forEach(function (menuItem) {
                    menuItem.dataset.type = "ComboBoxItem";
                });
            },

            resize: function () {
                var currentWidth = this.get("width");

                var buttonsWidth = 22;
                var domNodeBorderSize = 2;
                var textFieldDomNodeBorderSize = 2; //1 px from left and 1px from right
                var arrowNodeBorderSize = 1;

                if (currentWidth === "100%") {
                    currentWidth = this.domNode.getBoundingClientRect().width - domNodeBorderSize;
                    domNodeBorderSize = 0;  // need not subtract bordersize on menu's width
                }

                this._textField.domNode.style.width =
                    "calc(" + currentWidth + "px" + " - " + buttonsWidth + "px" + " - " + textFieldDomNodeBorderSize + "px" + " - " + arrowNodeBorderSize + "px)";

                this.menu.domNode.style.minWidth = "calc(" + currentWidth + "px" + " - " + domNodeBorderSize + "px)";
            },

            startup: function() {
                this.inherited(arguments);
                this.items.forEach(function (item) {
                    if(item.selected === true) {
                        this.set("value", item.value);
                    }
                }, this);
            },

            // overriding from standard widget
            _openComboBoxMenu: function() {
                this.inherited(arguments);
                // emit dropdownclick whenever menu is opened
                this.emit("dropdownclick", {});
            },

            closeMenu: function() {
                this.inherited(arguments);
                this.emit("restorefocustolastnonrefuseelement");
            },

            _setFooterIdAttr: function (footerId) {

                var footerWidget = registry.byId(footerId);

                // If a valid footer widget is returned, then create/replace the footerNode with the
                // footer widget and place it at footerNode.
                if (footerWidget) {
                    this._createFooterNode();
                    footerWidget.placeAt(this.footerNode);
                    footerWidget.startup();
                }

                this._set("footerId", footerId);
            },

            _createFooterNode: function () {

                if (!this.footerNode) {
                    this.footerNode = document.createElement("div");
                    this.get("menu").domNode.appendChild(this.footerNode);
                } else {
                    // footerNode exists so there must be an existing footer widget
                    // destroy the previous widget before placing the new footer widget
                    var oldFooterWidget = registry.byId(this.get("footerId"));
                    if (oldFooterWidget) {
                        oldFooterWidget.destroyRecursive();
                    }
                }

            },

            /**
             * Following are QE utility methods for callback testing. Overridden from mw-form/ComboBox.
             */
            _qeSelectItemByLabel: function (/* string */ _label) {
                var valueToSet = this._getSelectedItemFromLabel(_label).value; // get the value to set
                this._treatAsUserEvent = true;
                this.set('value', valueToSet, true);
                this._treatAsUserEvent = false;
            },

            _qeSelectItemByValue: function (/* string */ _value) {
                var valueToSet = this._getSelectedItemFromValue(_value).value; // get the value to set
                this._treatAsUserEvent = true;
                this.set('value', valueToSet, true);
                this._treatAsUserEvent = false;
            },

            _qeSelectItemByIndex: function (/* int */ index) {
                var valueToSet = this.get('items')[index].value; // get the value to set from items object
                this._treatAsUserEvent = true;
                this.set('value', valueToSet, true);
                this._treatAsUserEvent = false;
            }
        });
});
